'use client';

/**
 * TestArchLinksPanel - Architecture packages linked to a test case
 *
 * Fetches and displays ArchEntity nodes linked via TESTS edges.
 *
 * @layer presentation
 * @arch_component_type panel
 * @arch_domain testing
 */

import { useState, useEffect } from 'react';
import { Loader2, Layers, Package } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api/client';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';

interface ArchLink {
  semantic_id: string;
  name: string;
  type: string | null;
  description: string | null;
}

interface TestArchLinksPanelProps {
  entityId: string | null;
}

export function TestArchLinksPanel({ entityId }: TestArchLinksPanelProps) {
  const [links, setLinks] = useState<ArchLink[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const selectNode = useGraphSelectionStore((s) => s.selectNode);

  useEffect(() => {
    if (!entityId) return;
    const fetchLinks = async () => {
      setIsLoading(true);
      try {
        const data = await api.get<ArchLink[]>(`/testing/cases/${entityId}/arch-links`);
        setLinks(data);
      } catch {
        setLinks([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchLinks();
  }, [entityId]);

  if (!entityId) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-4">
        <Layers className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">Select a test case to see linked packages</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-4">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading architecture links...
      </div>
    );
  }

  if (!links || links.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-4">
        <Layers className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">No architecture links</p>
        <p className="text-xs mt-1">Run test sync to create TESTS edges</p>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-2">
      <div className="flex items-center gap-2 mb-3">
        <Layers className="w-4 h-4 text-muted-foreground" />
        <h4 className="text-xs font-semibold text-muted-foreground">
          ARCHITECTURE LINKS ({links.length})
        </h4>
      </div>
      {links.map((link) => (
        <div
          key={link.semantic_id}
          className="p-2 rounded bg-muted/30 border border-border cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() =>
            selectNode({
              id: link.semantic_id,
              type: link.type || 'package',
              label: link.name,
              properties: { description: link.description },
            })
          }
        >
          <div className="flex items-center gap-2">
            <Package className="w-3.5 h-3.5 text-blue-500 shrink-0" />
            <span className="text-sm font-medium">{link.name}</span>
            {link.type && (
              <Badge variant="secondary" className="text-[9px] px-1 ml-auto">
                {link.type}
              </Badge>
            )}
          </div>
          {link.description && (
            <p className="text-xs text-muted-foreground mt-1 ml-5.5 line-clamp-2">
              {link.description}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
